import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { ChildNotificationInterface, ItemCommunicationService, ParentNotificationInterface, ParentTriggerInterface } from '../../../services/item-communication.service';
import { Items } from 'tilby-models';
import { ItemCombinationsFields, ItemVariationsFormGroups } from './item-variations.model';
import { PriceListInterface } from 'src/app/features/items/utils/item.interface';

import {
  ItemDataOperationsService
} from 'src/app/features/items';

import { VariationsComponent } from './variations/variations.component';
import { CombinationsComponent } from './combinations/combinations.component';
import { ItemVariationsSectionService } from './item-variations-section.service';
import { CombinationsFormService } from './combinations/combinations-form.service';
import { VariationsFormService } from './variations/variations-form.service';
import { ConfirmDialogService } from 'src/app/dialogs';

@Component({
  selector: 'app-item-variations-section',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSlideToggleModule, MatChipsModule,
    VariationsComponent, CombinationsComponent
  ],
  templateUrl: './item-variations-section.component.html',
  styleUrl: './item-variations-section.component.scss'
})
export class ItemVariationsSectionComponent implements OnInit {

  readonly itemVariationsSectionService = inject(ItemVariationsSectionService);
  readonly variationsFormService = inject(VariationsFormService);
  private readonly combinationsFormService = inject(CombinationsFormService);
  private readonly itemCommunicationService = inject(ItemCommunicationService);
  private readonly itemDataOperationsService = inject(ItemDataOperationsService);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  @Input() item!: Items | undefined;

  @ViewChild(CombinationsComponent) combinationsComponent?: CombinationsComponent;
  @ViewChild(VariationsComponent) variationsComponent?: VariationsComponent;

  itemVariationsForm!: ItemVariationsFormGroups;
  priceList: Array<PriceListInterface> = [];
  disableCombinations: boolean = false;
  priceValue!: Record<string, number>;
  combinationsData: ItemCombinationsFields[] = []; 
  

  constructor() {
    effect(() => {
      // Listener per eseguire operazioni richieste dal parent
      const trigger = this.itemCommunicationService.parentTrigger();
      if(trigger?.child === 'variations' || trigger?.child === 'all') {
        this.handleParentTrigger(trigger);
      }
    }, { allowSignalWrites: true })

    effect(() => {
      // Listener per leggere notifiche inviate dal parent
      const notification = this.itemCommunicationService.parentNotifier();
      if (notification?.child === 'variations') {
        this.handleParentNotification(notification);
      }
    })
  }


  ngOnInit(): void {
    this.combinationsData = [...this.item?.combinations || []];
    this.itemVariationsForm = this.itemVariationsSectionService.createForm(this.item);
  }

  handleParentTrigger(message: ParentTriggerInterface) {
    if (message.trigger === 'tabActivated') {
      this.loadLazyData();
    }

    if (message.trigger === 'requestVariationData') {
      const requestedField = message.params?.formField;

      let dataToReturn = null;
      if (requestedField && this.itemVariationsForm.get(requestedField)) {
        dataToReturn = this.itemVariationsForm.get(requestedField)?.value;
      } 

      this.sendNotificationToParent({
        notification: 'variationData',
        receiver: message.sender,
        params: { [requestedField]: dataToReturn }
      });
    }

    if(message.trigger === 'cancel') {
      this.itemVariationsSectionService.resetFormValues(this.item);
      this.variationsFormService.resetFormValues(this.item?.variations || []);
      this.combinationsFormService.resetFormValues(this.item?.combinations || [], this.variationsFormService.variationsArray || []);
    }
  }

  loadLazyData() {
    this.requestStockFormData();
    this.requestGeneralFormData();
  }

  requestStockFormData() {
    setTimeout(() => {
      this.itemCommunicationService.createParentTrigger('requestStockData', 'stock', 'variations', { formField: 'stock_type' })
    })
  }

  requestGeneralFormData() {
    this.priceList = this.itemDataOperationsService.getPriceLists();
    const fieldsList = this.priceList.map(({priceFieldName}) => priceFieldName);
    setTimeout(() => {
      this.itemCommunicationService.createParentTrigger('requestGeneralData', 'general', 'variations', 
        { fieldsList: fieldsList })
    })
  }

  handleParentNotification(message: ParentNotificationInterface) {
    if(message.notification === 'stockData') {
      this.disableCombinations = message.params?.stock_type && message.params?.stock_type === 'bom' || false;
    } else if(message.notification === 'generalData') {
      this.priceValue = message.params;
    }
  }

  sendNotificationToParent(notification: ChildNotificationInterface) {
    Promise.resolve().then(() => {
      this.itemCommunicationService.notifyParent(notification);
    });
  }

  async onChangeIsGroupItem() {
    const isNowActive = this.itemVariationsSectionService.is_group_item?.value;

    if (isNowActive) {
      if (this.itemVariationsSectionService.combinationsActive?.value) {
        const proceed = await this.checkCombinationArray();
        if (!proceed) {
          this.itemVariationsSectionService.is_group_item?.setValue(false);
          return;
        }
      }
      this.itemVariationsSectionService.split_group_components?.setValue(false);
    }
  }

  async onChangeSplitGroupComponents() {
    const isNowActive = this.itemVariationsSectionService.split_group_components?.value;

    if (isNowActive) {
      // Controllo combinazioni
      if (this.itemVariationsSectionService.combinationsActive?.value) {
        const proceed = await this.checkCombinationArray();
        if (!proceed) {
          this.itemVariationsSectionService.split_group_components?.setValue(false);
          return;
        }
      }
      // Spengo il flag is_group_item
      this.itemVariationsSectionService.is_group_item?.setValue(false);
    }
  }

  async onChangeCombinationsActive() {
    const isNowActive = this.itemVariationsSectionService.combinationsActive?.value;

    if (isNowActive) {
      this.itemVariationsSectionService.is_group_item?.setValue(false);
      this.itemVariationsSectionService.split_group_components?.setValue(false);
    } else {
      await this.checkCombinationArray();
    }
  }

  async checkCombinationArray(): Promise<boolean> {
    if (this.combinationsFormService.combinationsArray?.length) {
      let confirm = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'ITEMS.DETAILS.WANT_TO_DISABLE_COMBINATIONS',
        }
      });
      if (confirm) {
        this.combinationsFormService.removeAllCombinations();
        this.combinationsData = [];
        this.itemVariationsSectionService.combinationsActive?.setValue(false);
        return true;
      } else {
        this.itemVariationsSectionService.combinationsActive?.setValue(true);
        return false;
      }
    } else {
      this.combinationsFormService.removeAllCombinations();
      this.combinationsData = [];
      this.itemVariationsSectionService.combinationsActive?.setValue(false);
      return true;
    }
  }

  async handleRemoveVariation(variationIndex: number) {
    // Recupero gli indici delle combinazioni da eliminare
    const conflictingIndices = this.combinationsFormService.getIndicesUsingVariation(variationIndex);
    
    let canDelete = true;

    // Se ci sono conflitti, apro il dialog
    if (conflictingIndices.length > 0) {
      const confirmed = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'ITEMS.DETAILS.VARIATION_DELETE_CONFIRMATIONS',
        }
      });

      if (confirmed) {
        this.combinationsFormService.removeCombinationsByIndices(conflictingIndices);
      } else {
        canDelete = false;
      }
    }

    // Se posso procedere, cancello la variazione
    if (canDelete) {
      this.doRemoveVariation(variationIndex);
    }
  }

  private doRemoveVariation(index: number) {
    // Rimuove la variante dal form
    this.variationsFormService.removeVariation(index);
    
    // Rimuove la variante dalle combinazioni rimaste
    this.combinationsFormService.removeVariationColumnFromAllCombinations(index);

    // Aggiorna observables 
    this.combinationsFormService.updateVariationValueNameListener.update(v => v + 1);
  }

  handleRemoveVariationValue(event: {variationIndex: number, variationValueIndex: number}) {
    const variationIndex = event.variationIndex;
    const variationValueIndex = event.variationValueIndex;

    if(variationIndex > -1 && variationValueIndex > -1) {
      const variationValueToDelete = this.variationsFormService.getNameVariationValues(variationIndex, variationValueIndex)?.value;
  
      this.variationsFormService.removeVariationValue(variationIndex, variationValueIndex);
  
      this.combinationsFormService.combinationsArray?.controls.forEach((combination, combinationIndex)=> {
        this.combinationsFormService.removeVariationValueCombination(combinationIndex, variationIndex, variationValueIndex);
        if(this.combinationsFormService.getVariationValueCombination(combinationIndex, variationIndex)?.value &&
          this.combinationsFormService.getVariationValueCombination(combinationIndex, variationIndex)?.value?.value == variationValueToDelete) 
        {
          this.combinationsFormService.getVariationValueCombination(combinationIndex, variationIndex)?.setValue(null);
        }
      })
      this.combinationsFormService.updateVariationValueNameListener.update(v => v + 1);
      const variationValues = this.variationsFormService.variationsArray?.value.at(variationIndex)?.variation_values || [];
      this.combinationsFormService.updateVariationValueValidators(variationIndex, variationValues);
    }
  }

  handleCloseCombinationsPanel() {
    this.combinationsComponent?.closeAllCombinationPanels();
  }

  handleAddVariation(variationName: string) {
    this.variationsComponent?.addNewVariation(variationName);
  }

  handleAddVariationValue(event: {index: number, variationValueName: string}) {
    this.variationsComponent?.addVariationValue(event.index, event.variationValueName);
  }

  handleCloseVariationsPanel() {
    this.variationsComponent?.closeAllVariationValuePanels();
    this.variationsComponent?.closeAllVariantPanels();
  }
}
